import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from "@headlessui/react";
import { CheckCircleIcon, ExclamationCircleIcon, InformationCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { Fragment, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  closeAllModals,
  closeModal,
} from "../../../app/features/slices/modalSlice";

const ReduxModal = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const location = useLocation();
  const { modals } = useSelector((state) => state.modal);

  // Close all modals whenever path changes
  useEffect(() => {
    if (modals.length > 0) {
      dispatch(closeAllModals());
    }
  }, [location.pathname, dispatch]);

  const handleClose = (modalId) => {
    dispatch(closeModal({ id: modalId }));
  };

  // Helper to map bootstrap classes or sizes to Tailwind max-width
  const getMaxWidthClass = (containerClass) => {
    if (!containerClass) return "max-w-2xl"; // Default
    if (containerClass.includes("modal-sm")) return "max-w-sm";
    if (containerClass.includes("modal-lg")) return "max-w-4xl";
    if (containerClass.includes("modal-xl")) return "max-w-6xl";
    if (containerClass.includes("modal-fullscreen")) return "max-w-full m-4";
    return "max-w-2xl";
  };

  const getIcon = (title) => {
    // Simple heuristic for icons based on title or context usage
    // You might want to pass an iconType in the payload in the future
    const lowerTitle = (title || "").toLowerCase();
    if (lowerTitle.includes("error") || lowerTitle.includes("delete") || lowerTitle.includes("warning")) {
      return <ExclamationCircleIcon className="h-6 w-6 text-red-500" aria-hidden="true" />;
    }
    if (lowerTitle.includes("success")) {
      return <CheckCircleIcon className="h-6 w-6 text-green-500" aria-hidden="true" />;
    }
    if (lowerTitle.includes("info")) {
      return <InformationCircleIcon className="h-6 w-6 text-blue-500" aria-hidden="true" />;
    }
    return <InformationCircleIcon className="h-6 w-6 text-indigo-500" aria-hidden="true" />;
  };

  if (modals.length === 0) return null;

  return (
    <>
      {modals.map((modal, index) => {
        const {
          id,
          modalTitle,
          modalContent,
          modalContainerClass,
          closable,
        } = modal;

        // Ensure each modal sits on top of the previous one
        // Headless UI handles z-index automatically properly via Portal if rendered sequentially
        // But we can add specific z-index if needed for other app layers
        const zIndexBase = 50;

        return (
          <Transition appear show={true} as={Fragment} key={id}>
            <Dialog
              as="div"
              className="relative"
              style={{ zIndex: zIndexBase + index * 10 }}
              onClose={() => closable && handleClose(id)}
            >
              <TransitionChild
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity" />
              </TransitionChild>

              <div className="fixed inset-0 z-10 w-screen">
                <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                  <TransitionChild
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    enterTo="opacity-100 translate-y-0 sm:scale-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                    leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                  >
                    <DialogPanel
                      className={`relative transform rounded-xl bg-white text-left shadow-2xl transition-all sm:my-8 w-full ${getMaxWidthClass(modalContainerClass)}`}
                    >
                      {/* Header */}
                      <div className="bg-gray-50 px-4 py-3 sm:px-6 flex items-center justify-between border-b border-gray-100 rounded-t-xl">
                        <div className="flex items-center gap-3">
                          {getIcon(modalTitle)}
                          <DialogTitle as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                            {t(modalTitle)}
                          </DialogTitle>
                        </div>
                        {closable && (
                          <button
                            type="button"
                            className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors"
                            onClick={() => handleClose(id)}
                          >
                            <span className="sr-only">Close</span>
                            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                          </button>
                        )}
                      </div>

                      {/* Content */}
                      <div className="max-h-[80vh] overflow-auto custom-scrollbar-thin rounded-b-xl">
                        {modalContent}
                      </div>

                    </DialogPanel>
                  </TransitionChild>
                </div>
              </div>
            </Dialog>
          </Transition>
        );
      })}
    </>
  );
};

export default ReduxModal;
